/**
 * Session state machine.
 *
 * boot: refresh-token present → silent refresh → /v1/me → signedIn.
 * signIn*(...) wrap the real Auth calls; signOut revokes + clears.
 * The Clinic backend stays authoritative — this only holds client state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  completeTemporaryPassword,
  loginWithPassword,
  logout as apiLogout,
  otpVerify,
} from './api';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearSession,
  getStoredRefreshToken,
  onSessionCleared,
  refreshAccessToken,
  subscribeSessionStorage,
} from './tokens';
import { fetchMe, type MeResponse } from '../authz/api';
import { setSelectedClinicId, unfreezeClinicContext } from '../authz/clinicContext';
import { noteSessionEnded } from './returnTo';

export type SessionState =
  { status: 'booting' } | { status: 'signedOut' } | { status: 'signedIn'; me: MeResponse };

interface SessionContextValue {
  session: SessionState;
  /** Each sign-in resolves to the signed-in identity, so the caller can continue as THAT person. */
  signInPassword: (identifier: string, password: string) => Promise<MeResponse>;
  /** ZOY-283: replace a temporary password and open the session in one step. */
  completeFirstLogin: (
    identifier: string,
    currentPassword: string,
    newPassword: string,
  ) => Promise<MeResponse>;
  signInOtp: (phone: string, code: string) => Promise<MeResponse>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SessionState>({ status: 'booting' });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const completeSignIn = useCallback(async () => {
    const me = await fetchMe();
    if (alive.current) setSession({ status: 'signedIn', me });
    return me;
  }, []);

  // Boot: try to resume from the stored refresh token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getStoredRefreshToken() === null) {
        setSession({ status: 'signedOut' });
        return;
      }
      try {
        await refreshAccessToken();
        const me = await fetchMe();
        if (!cancelled) setSession({ status: 'signedIn', me });
      } catch {
        clearSession();
        if (!cancelled) setSession({ status: 'signedOut' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A cleared session (refresh refused mid-flight, or a sign-out in ANOTHER
  // tab — ZOY-610) signs the UI out and empties the cache with it (ZOY-351).
  // The uid whose session ended is noted so a return-to stays theirs.
  useEffect(
    () =>
      onSessionCleared(() => {
        setSelectedClinicId(null);
        unfreezeClinicContext();
        queryClient.clear();
        setSession((s) => {
          if (s.status === 'signedIn') noteSessionEnded(s.me.uid);
          return s.status === 'signedIn' ? { status: 'signedOut' } : s;
        });
      }),
    [queryClient],
  );
  useEffect(() => subscribeSessionStorage(), []);

  const signInPassword = useCallback(
    async (identifier: string, password: string) => {
      await loginWithPassword(identifier, password);
      return completeSignIn();
    },
    [completeSignIn],
  );

  const completeFirstLogin = useCallback(
    async (identifier: string, currentPassword: string, newPassword: string) => {
      await completeTemporaryPassword({ identifier, currentPassword, newPassword });
      return completeSignIn();
    },
    [completeSignIn],
  );

  const signInOtp = useCallback(
    async (phone: string, code: string) => {
      await otpVerify(phone, code);
      return completeSignIn();
    },
    [completeSignIn],
  );

  const signOut = useCallback(async () => {
    await apiLogout();
    setSelectedClinicId(null);
    unfreezeClinicContext();
    // ZOY-351: cached data (the permission snapshot included) never
    // outlives the session it belongs to. The remembered clinic
    // (zc.clinic.<uid>) deliberately stays: it is per uid, so the same person
    // lands back where they were and nobody else inherits it (Product 2026-09-08).
    queryClient.clear();
    setSession((s) => {
      if (s.status === 'signedIn') noteSessionEnded(s.me.uid);
      return { status: 'signedOut' };
    });
  }, [queryClient]);

  const value = useMemo(
    () => ({ session, signInPassword, completeFirstLogin, signInOtp, signOut }),
    [session, signInPassword, completeFirstLogin, signInOtp, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
